import React, { useState, useEffect } from 'react';
import { Trade } from '../../types/Trade';
import { RowTab, ParquetFormState } from '../EditTab/types';
import { useParquetStore } from '../EditTab/useParquetStore';
import { generateParquet, downloadBlob } from '../EditTab/api';
import SearchBar from '../EditTab/components/SearchBar';
import FilterBar from '../EditTab/components/FilterBar';
import RowTabCard from '../EditTab/components/RowTabCard';
import CreateParquetModal from '../EditTab/components/CreateParquetModal';
import ActivityLog from '../../shared/ActivityLog/ActivityLog';
import UserGuide from './UserGuide';
import '../EditTab/EditTab.css';
import './HomeTab.css';

type HomeSubTab = 'guide' | 'dashboard' | 'add' | 'activity';

interface HomeTabProps {
  trades: Trade[];
  onEdit: (id: string, changes: Partial<Trade>) => void;
}

export default function HomeTab(_props: HomeTabProps) {
  const [subTab, setSubTab] = useState<HomeSubTab>('dashboard');

  const {
    rowTabs,
    allRowTabs,
    searchQuery,
    setSearchQuery,
    timeframeFilter,
    setTimeframeFilter,
    filterDateFrom,
    setFilterDateFrom,
    filterDateTo,
    setFilterDateTo,
    addRowTab,
    updateRowTab,
    deleteRowTab,
    refresh: refreshParquet,
  } = useParquetStore();

  // Re-fetch data whenever the sub-tab becomes active
  useEffect(() => {
    if (subTab === 'dashboard') refreshParquet();
  }, [subTab, refreshParquet]);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingTab, setEditingTab] = useState<RowTab | null>(null);
  const [loading, setLoading] = useState(false);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [downloadBytes, setDownloadBytes] = useState<number>(0);
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);

  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 5000);
  };

  const makeFilename = (
    name: string,
    source: string,
    from: string,
    to: string,
  ) => `${name.trim().replace(/\s+/g, '_')}_${source}_${from}_${to}.zip`;

  const handleCreate = async (form: ParquetFormState) => {
    addRowTab(form);
    setModalOpen(false);

    setLoading(true);
    try {
      const blob = await generateParquet(form);
      downloadBlob(
        blob,
        makeFilename(form.name, form.dataSource, form.dateFrom, form.dateTo),
      );
      showToast(`"${form.name}" created and downloading.`, 'success');
    } catch (err: any) {
      showToast(
        `Row tab saved, but download failed: ${err.message || 'Could not reach backend. Is ClickHouse connected?'}`,
        'error',
      );
    } finally {
      setLoading(false);
    }
  };

  const handleEditSubmit = async (form: ParquetFormState) => {
    if (!editingTab) return;
    updateRowTab(editingTab.id, form);
    setEditingTab(null);

    setLoading(true);
    try {
      const blob = await generateParquet(form);
      downloadBlob(
        blob,
        makeFilename(form.name, form.dataSource, form.dateFrom, form.dateTo),
      );
      showToast(`"${form.name}" updated and downloading.`, 'success');
    } catch (err: any) {
      showToast(
        `Row tab updated, but download failed: ${err.message || 'Could not reach backend. Is ClickHouse connected?'}`,
        'error',
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (rowTab: RowTab) => {
    setDownloadingId(rowTab.id);
    setDownloadBytes(0);
    // Throttle progress updates so the React tree re-renders ~4×/sec, not on
    // every TCP chunk — counter still feels live without thrashing.
    let lastTick = 0;
    const onProgress = (bytes: number) => {
      const now = performance.now();
      if (now - lastTick > 250) {
        lastTick = now;
        setDownloadBytes(bytes);
      }
    };
    try {
      const blob = await generateParquet(
        {
          name: rowTab.name,
          timeframes: rowTab.timeframes,
          dataSource: rowTab.dataSource,
          dataTypes: rowTab.dataTypes,
          coins: rowTab.coins,
          dateFrom: rowTab.dateFrom,
          dateTo: rowTab.dateTo,
        },
        onProgress,
      );
      downloadBlob(
        blob,
        makeFilename(
          rowTab.name,
          rowTab.dataSource,
          rowTab.dateFrom,
          rowTab.dateTo,
        ),
      );
      showToast(`Downloading "${rowTab.name}"...`, 'success');
    } catch (err: any) {
      showToast(
        `Download failed: ${err.message || 'Could not reach backend. Is ClickHouse connected?'}`,
        'error',
      );
    } finally {
      setDownloadingId(null);
      setDownloadBytes(0);
    }
  };

  const handleDelete = (rowTab: RowTab) => {
    deleteRowTab(rowTab.id);
    showToast(`"${rowTab.name}" deleted.`, 'success');
  };

  const openEdit = (rowTab: RowTab) => {
    setEditingTab(rowTab);
  };

  const editInitialValues: ParquetFormState | undefined = editingTab
    ? {
        name: editingTab.name,
        timeframes: editingTab.timeframes,
        dataSource: editingTab.dataSource,
        dataTypes: editingTab.dataTypes,
        coins: editingTab.coins,
        dateFrom: editingTab.dateFrom,
        dateTo: editingTab.dateTo,
      }
    : undefined;

  return (
    <div className="home-page">
      <div className="home-subtab-bar">
        <button
          className={`home-subtab ${subTab === 'guide' ? 'active' : ''}`}
          onClick={() => setSubTab('guide')}
        >
          Getting Started
        </button>
        <button
          className={`home-subtab ${subTab === 'dashboard' ? 'active' : ''}`}
          onClick={() => setSubTab('dashboard')}
        >
          Dashboard
        </button>
        <button
          className={`home-subtab ${subTab === 'activity' ? 'active' : ''}`}
          onClick={() => setSubTab('activity')}
        >
          My Activity
        </button>
      </div>

      <div className="home-content">
        {/* Toast notification — shared by Dashboard and Presets actions */}
        {toast && (
          <div
            className={`pq-toast pq-toast--${toast.type}`}
            onClick={() => setToast(null)}
          >
            <span className="pq-toast-icon">
              {toast.type === 'success' ? '\u2713' : '\u2717'}
            </span>
            <span className="pq-toast-msg">{toast.message}</span>
          </div>
        )}

        {/* ── Getting Started sub-tab ── */}
        {subTab === 'guide' && <UserGuide />}

        {/* ── Dashboard sub-tab ── */}
        {subTab === 'dashboard' && (
          <div className="pq-dashboard">
            <div className="pq-dashboard-header">
              <div className="pq-header-left">
                <h2 className="pq-title">Parquet Dashboard</h2>
                <p className="pq-subtitle">
                  {allRowTabs.length} row tab
                  {allRowTabs.length !== 1 ? 's' : ''} created
                </p>
              </div>
              <button
                className="pq-btn pq-btn-create"
                onClick={() => setModalOpen(true)}
              >
                <span className="pq-btn-icon">+</span> Create Parquet File
              </button>
            </div>

            <div className="pq-toolbar">
              <SearchBar
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder="Search by name or coin..."
              />
              <FilterBar
                selectedTimeframe={timeframeFilter}
                onTimeframeChange={setTimeframeFilter}
                dateFrom={filterDateFrom}
                dateTo={filterDateTo}
                onDateFromChange={setFilterDateFrom}
                onDateToChange={setFilterDateTo}
              />
            </div>

            {rowTabs.length === 0 ? (
              <div className="pq-empty-state">
                {allRowTabs.length === 0 ? (
                  <>
                    <div className="pq-empty-icon">&#128230;</div>
                    <h3>No Row Tabs Yet</h3>
                    <p>
                      Create your first Parquet file configuration to get
                      started.
                    </p>
                    <button
                      className="pq-btn pq-btn-primary"
                      onClick={() => setModalOpen(true)}
                    >
                      Create Parquet File
                    </button>
                  </>
                ) : (
                  <>
                    <div className="pq-empty-icon">&#128269;</div>
                    <h3>No Results</h3>
                    <p>
                      No row tabs match your current search or filter criteria.
                    </p>
                  </>
                )}
              </div>
            ) : (
              <div className="pq-grid">
                {rowTabs.map((tab) => (
                  <RowTabCard
                    key={tab.id}
                    rowTab={tab}
                    onEdit={openEdit}
                    onDownload={handleDownload}
                    onDelete={handleDelete}
                    downloading={downloadingId === tab.id}
                    downloadBytes={downloadingId === tab.id ? downloadBytes : 0}
                  />
                ))}
              </div>
            )}

            <CreateParquetModal
              open={modalOpen}
              onClose={() => setModalOpen(false)}
              onSubmit={handleCreate}
              loading={loading}
              mode="create"
              existingNames={allRowTabs.map((t) => t.name)}
            />
            <CreateParquetModal
              open={!!editingTab}
              onClose={() => setEditingTab(null)}
              onSubmit={handleEditSubmit}
              initialValues={editInitialValues}
              loading={loading}
              mode="edit"
              existingNames={allRowTabs.map((t) => t.name)}
            />
          </div>
        )}

        {/* ── My Activity sub-tab ── */}
        {subTab === 'activity' && <ActivityLog />}
      </div>
    </div>
  );
}
